import React, { Suspense, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import HubBackground from '../effects/HubBackground';
import { getCtaShaderProps, getFeaturesShaderProps } from '../effects/shaderGradientPresets.js';
import { useProducts, getProductIcon, ProductPriceLabel } from '../../../hooks/useProducts.jsx';
import { useIsMobile } from '../../../hooks/useIsMobile.js';

/**
 * ランディングページ
 */
const LandingPage = () => {
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState('hero');
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const modulesRef = useRef(null);
  const ctaRef = useRef(null);
  
  // CMSから製品データを取得
  const { products, loading: productsLoading } = useProducts();

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    if (heroRef.current) observer.observe(heroRef.current);
    if (featuresRef.current) observer.observe(featuresRef.current);
    if (modulesRef.current) observer.observe(modulesRef.current);
    if (ctaRef.current) observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  // Sync activeSection with header styling via body class
  useEffect(() => {
    if (activeSection === 'hero') {
      document.body.classList.add('landing-hero-active');
    } else {
      document.body.classList.remove('landing-hero-active');
    }
    return () => {
      document.body.classList.remove('landing-hero-active');
    };
  }, [activeSection]);

  // Panel animation observer - once visible, stays visible（useLayoutEffect で初回 flash 防止）
  useLayoutEffect(() => {
    const createShowObserver = (rootMargin) => {
      return new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
            }
          });
        },
        {
          root: null,
          rootMargin,
          threshold: 0.01
        }
      );
    };

    const featuresTopRowObserver = createShowObserver('200px 0px -5% 0px');
    const featuresBottomRowObserver = createShowObserver('300px 0px -5% 0px');
    const modulesPanelObserver = createShowObserver('250px 0px -5% 0px');

    const featurePanels = document.querySelectorAll('.hub-features .hub-panel-animated');
    featurePanels.forEach((panel, index) => {
      if (index < 3) {
        featuresTopRowObserver.observe(panel);
      } else {
        featuresBottomRowObserver.observe(panel);
      }
    });

    document.querySelectorAll('.hub-modules .hub-panel-animated')
      .forEach((panel) => modulesPanelObserver.observe(panel));

    return () => {
      featuresTopRowObserver.disconnect();
      featuresBottomRowObserver.disconnect();
      modulesPanelObserver.disconnect();
    };
  }, [products.length]);

  return (
    <>
      {/* Fixed Gradient Backgrounds */}
      <div className="hub-fixed-gradient-container">
        {/* BG00 - Hero (ColorBends) / BG00_mobil = Iridescence on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'hero' ? 'active' : ''}`}>
          <HubBackground isMobile={isMobile} />
        </div>

        {/* BG01 - CTA (ShaderGradient) / BG01_mobil on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'cta' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient {...getCtaShaderProps(isMobile)} />
            </Suspense>
          </ShaderGradientCanvas>
        </div>

        {/* BG02 - Features / BG02_mobil on mobile */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'features' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient {...getFeaturesShaderProps(isMobile)} />
            </Suspense>
          </ShaderGradientCanvas>
        </div>

        {/* BG03 - Modules */}
        <div className={`hub-fixed-gradient-layer ${activeSection === 'modules' ? 'active' : ''}`}>
          <ShaderGradientCanvas
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={null}>
              <ShaderGradient
                animate="on"
                axesHelper="off"
                bgColor1="#000000"
                bgColor2="#000000"
                brightness={1.2}
                cAzimuthAngle={180}
                cDistance={2.5}
                cPolarAngle={120}
                cameraZoom={1}
                color1="#ebedff"
                color2="#f3f2f8"
                color3="#dbf8ff"
                destination="onCanvas"
                embedMode="off"
                envPreset="city"
                format="gif"
                fov={45}
                frameRate={10}
                gizmoHelper="hide"
                grain="off"
                lightType="3d"
                pixelDensity={1}
                positionX={0}
                positionY={1.8}
                positionZ={0}
                range="disabled"
                rangeEnd={40}
                rangeStart={0}
                reflection={0.1}
                rotationX={0}
                rotationY={0}
                rotationZ={-90}
                shader="defaults"
                type="waterPlane"
                uAmplitude={0}
                uDensity={1.3}
                uFrequency={5.5}
                uSpeed={0.3}
                uStrength={3}
                uTime={0.2}
                wireframe={false}
                zoomOut={false}
              />
            </Suspense>
          </ShaderGradientCanvas>
        </div>
      </div>

      {/* ヒーローセクション */}
      <section className="hub-hero hub-section-dark" ref={heroRef} data-section="hero">
        <div className="hub-hero-inner">
          <div className="hub-hero-title-wrap">
            <h1 className="hub-hero-title">
              <span className="hub-hero-title-shadow">歯科医療の未来を、</span><br />
              <span className="hub-hero-title-gradient">ひとつのプラットフォーム</span><span className="hub-hero-title-shadow">で。</span>
            </h1>
          </div>

          <p className="hub-hero-subtitle">
            Synapseは、AI診断支援からデジタル歯式管理、在庫管理システムまで、
            歯科医療に必要なあらゆるモジュールを提供する総合SaaSプラットフォームです。
          </p>

          <div className="hub-hero-actions">
            <Link to="/signup" className="hub-btn hub-btn-gradient hub-btn-lg">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
              無料トライアルを開始
            </Link>
            <Link to="/products" className="hub-btn hub-btn-glass-dark hub-btn-lg">
              製品を見る
            </Link>
          </div>
          
          <div className="hub-hero-stats">
            <div className="hub-hero-stat">
              <div className="hub-hero-stat-value">500+</div>
              <div className="hub-hero-stat-label">導入クリニック</div>
            </div>
            <div className="hub-hero-stat">
              <div className="hub-hero-stat-value">99.9%</div>
              <div className="hub-hero-stat-label">稼働率</div>
            </div>
            <div className="hub-hero-stat">
              <div className="hub-hero-stat-value">98%</div>
              <div className="hub-hero-stat-label">導入満足度</div>
            </div>
          </div>
        </div>
      </section>

      {/* 特長セクション */}
      <section className="hub-features hub-section-dark" ref={featuresRef} data-section="features">
        <div className="hub-features-inner">
          <div className="hub-section-header">
            <div className="hub-section-badge">Features</div>
            <h2 className="hub-section-title">なぜSynapseなのか</h2>
            <p className="hub-section-subtitle">
              単なるソフトウェアではなく、歯科医療のパートナーとして
            </p>
          </div>
          
          <div className="hub-features-grid">
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">モジュール式設計</h3>
              <p className="hub-feature-description">
                必要な機能だけを選んで導入。クリニックの規模や専門分野に合わせて
                最適なモジュールの組み合わせをカスタマイズできます。
              </p>
            </div>
            
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">医療グレードのセキュリティ</h3>
              <p className="hub-feature-description">
                患者データは最高レベルのセキュリティで保護。
                SOC2準拠、データの暗号化、定期的なセキュリティ監査を実施しています。
              </p>
            </div>
            
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">AI駆動の診断支援</h3>
              <p className="hub-feature-description">
                最新のAI技術を活用した診断支援ツール。
                診断画像の自動解析、疾病リスク評価など、高度な診断をサポートします。
              </p>
            </div>
            
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">シームレスな統合</h3>
              <p className="hub-feature-description">
                すべてのモジュールはひとつのプラットフォームで統合。
                データの二重入力や異なるシステム間の連携の煩わしさから解放されます。
              </p>
            </div>
            
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">チームコラボレーション</h3>
              <p className="hub-feature-description">
                医師、スタッフ、受付のそれぞれに最適化された権限管理。
                チーム全体で効率的な情報共有が可能です。
              </p>
            </div>
            
            <div className="hub-feature-card hub-panel-animated">
              <div className="hub-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3 className="hub-feature-title">丁寧なサポート</h3>
              <p className="hub-feature-description">
                専門のサポートチームが導入から運用まで丁寧にサポート。
                クリニックの運営に寄り添い、あらゆる疑問にお答えします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* モジュール紹介セクション */}
      <section className="hub-modules hub-section-light" ref={modulesRef} data-section="modules">
        <div className="hub-modules-inner">
          <div className="hub-section-header">
            <div className="hub-section-badge">Modules</div>
            <h2 className="hub-section-title">選べるモジュール</h2>
            <p className="hub-section-subtitle">
              クリニックのニーズに合わせて、必要なモジュールを選択
            </p>
          </div>
          
          <div className="hub-modules-grid">
            {productsLoading ? (
              <div className="hub-module-loading">読み込み中...</div>
            ) : (
              products.slice(0, 3).map((product) => (
                <div key={product.id} className="hub-module-card hub-panel-animated">
                  <div className="hub-module-header">
                    <div className="hub-module-icon">
                      {getProductIcon(product.iconType)}
                    </div>
                    <div className="hub-module-info">
                      <h3>{product.name}</h3>
                      <p>{product.shortDescription}</p>
                    </div>
                  </div>
                  <ul className="hub-module-features">
                    {(product.features || []).slice(0, 3).map((feature, idx) => (
                      <li key={idx}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="hub-module-footer">
                    <div className="hub-module-price">
                      {product.isComingSoon && (
                        <span className="hub-badge hub-badge-warning">準備中</span>
                      )}
                      <span className={product.isFree ? 'hub-price-free' : ''}>
                        <ProductPriceLabel product={product} />
                      </span>
                    </div>
                    <Link to="/products" className="hub-btn hub-btn-secondary">詳細を見る</Link>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {products.length > 3 && (
            <div className="hub-modules-more">
              <Link to="/products" className="hub-btn hub-btn-ghost">
                すべてのモジュールを見る →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTAセクション */}
      <section className="hub-cta hub-section-dark" ref={ctaRef} data-section="cta">
        <div className="hub-cta-inner">
          <h2 className="hub-cta-title">
            今すぐ始めましょう
          </h2>
          <p className="hub-cta-subtitle">
            14日間の無料トライアルで、Synapseの全機能をお試しいただけます。
          </p>
          <div className="hub-cta-actions">
            <Link to="/signup" className="hub-btn hub-btn-gradient hub-btn-lg">
              無料トライアルを開始
            </Link>
            <Link to="/products" className="hub-btn hub-btn-glass-dark hub-btn-lg">
              製品を見る
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LandingPage;

